import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const SOURCE_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{11,127}$/u;
const NUMBER_PATTERN = /^GH-(\d{4})(\d{2})(\d{2})-([0-9A-F]{12})$/u;
const FNV_1A_64_OFFSET = 0xcbf29ce484222325n;
const FNV_1A_64_PRIME = 0x100000001b3n;
const LOWER_48_BITS = 0xffffffffffffn;

export const GroupBuyReconciliationNumberSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^GH-[0-9]{8}-[0-9A-F]{12}$",
});
export type GroupBuyReconciliationNumber = Static<typeof GroupBuyReconciliationNumberSchema>;

function isRealBusinessDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString() === `${value}T00:00:00.000Z`
  );
}

function hashSuffix(value: string): string {
  let hash = FNV_1A_64_OFFSET;
  for (const character of value) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * FNV_1A_64_PRIME);
  }
  return (hash & LOWER_48_BITS).toString(16).toUpperCase().padStart(12, "0");
}

export function deriveGroupBuyReconciliationNumber(
  businessDate: string,
  reconciliationId: string,
): GroupBuyReconciliationNumber {
  if (!isRealBusinessDate(businessDate)) {
    throw new TypeError("businessDate must be a real calendar date in YYYY-MM-DD format");
  }
  if (!SOURCE_ID_PATTERN.test(reconciliationId)) {
    throw new TypeError("reconciliationId must contain at least 12 safe characters");
  }
  return `GH-${businessDate.replaceAll("-", "")}-${hashSuffix(
    `GH\0${businessDate}\0${reconciliationId}`,
  )}`;
}

export function isGroupBuyReconciliationNumber(
  value: unknown,
): value is GroupBuyReconciliationNumber {
  if (!Value.Check(GroupBuyReconciliationNumberSchema, value)) return false;
  const match = NUMBER_PATTERN.exec(value);
  return Boolean(match && isRealBusinessDate(`${match[1]}-${match[2]}-${match[3]}`));
}
